import { useQuery, keepPreviousData, type QueryKey } from '@tanstack/react-query'
import type { PaginatedResponse } from './api-client'

/**
 * Page-based variant of the list hooks for table views (GET /api/<resource> →
 * { data, total, limit, offset }). Unlike usePaginatedList (infinite scroll),
 * this fetches one page at a time so the DataTable can show "Page X of Y" with
 * server-side pagination. keepPreviousData avoids a flash while paging.
 */
export function usePagedList<T>(
  key: QueryKey,
  pagination: { pageIndex: number; pageSize: number },
  fetchPage: (range: { limit: number; offset: number }) => Promise<PaginatedResponse<T>>,
  options: { refetchInterval?: number } = {},
) {
  const baseKey = Array.isArray(key) ? key : [key]
  const query = useQuery({
    ...options,
    queryKey: [...baseKey, pagination.pageIndex, pagination.pageSize],
    queryFn: () =>
      fetchPage({
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      }),
    placeholderData: keepPreviousData,
  })

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize))

  return { ...query, rows, total, pageCount }
}
